// Copyright 2022, Imprivata, Inc.  All rights reserved.

import { catchError, filter, from, of, switchMap } from 'rxjs';
import { isActionOf } from 'typesafe-actions';
import { jsonToBase64 } from '@imprivata-cloud/dataprivacy';
import {
  addSpanEvent,
  createSpan,
  stringifyLoggable,
} from '@imprivata-cloud/common';
import type { Epic } from 'redux-observable';
import type { Action, EpicDependencies, RootState } from '../types';
import type { ApiError } from '../../api/types';
import { ApplicationAccessStartResponseNextAction } from '../../shared/types/api';
import {
  getCredentialsAction,
  saveCodingContextAction,
  setAuthenticationStatusAction,
  startApplicationAccessAction,
} from '../actions';
import { sendAuthnDataToAgent } from '../../agent-api';
import {
  isSharedWorkstation,
  normalizeSamlDataForEpicOauth,
} from '../../utils/utils';
import { AuthenticationResultStatusCode } from '../../constants';
import { SpanNames } from '../../tracing/constants';

export const startApplicationAccessEpic: Epic<
  Action,
  Action,
  RootState,
  Pick<EpicDependencies, 'startApplicationAccess'>
> = (action$, state$, dependencies) => {
  const { startApplicationAccess } = dependencies;

  return action$.pipe(
    filter(isActionOf(startApplicationAccessAction.request)),
    switchMap(({ payload }) => {
      createSpan(SpanNames.START_CLIENT_USER_SESSION);
      const { agentSaml, appAccessContext } = state$.value;

      const { baseUrl, fhirServerUrl, isProduction } = appAccessContext || {};

      const {
        contextType,
        resourceType,
        codingContext,
        oidcAuthorizationCode,
        identityToken,
      } = payload;

      let samlData;

      // TODO: Remove or replace when Epic Oauth flow is clarified
      const { samlAccepted, samlRequired } =
        normalizeSamlDataForEpicOauth(appAccessContext?.samlData) || {};

      if (appAccessContext?.samlData) {
        samlData = jsonToBase64(appAccessContext.samlData);
      }

      if (isSharedWorkstation(appAccessContext)) {
        sendAuthnDataToAgent(
          {
            username: appAccessContext?.username,
            password: appAccessContext?.password,
            domain: appAccessContext?.domain,
          },
          oidcAuthorizationCode,
        );
        return of(getCredentialsAction.success());
      }

      return from(
        startApplicationAccess(
          {
            contextType,
            resourceType,
            epicData: {
              appSamlData: agentSaml || samlData || '',
              samlAccepted,
              samlRequired,
              interconnectBaseUrl: baseUrl,
              fhirServerUrl,
              isProduction,
            },
          },
          codingContext,
          identityToken,
        ),
      ).pipe(
        switchMap(response => {
          addSpanEvent(SpanNames.START_CLIENT_USER_SESSION, {
            response: stringifyLoggable(response),
          });
          if (
            response?.nextAction ===
            ApplicationAccessStartResponseNextAction.GET_APP_CREDENTIALS
          ) {
            if (codingContext) {
              return from([
                saveCodingContextAction(codingContext),
                getCredentialsAction.request(payload),
              ]);
            } else {
              return of(getCredentialsAction.request(payload));
            }
          } else {
            return of(startApplicationAccessAction.failure());
          }
        }),
        catchError((error: ApiError) => {
          addSpanEvent(SpanNames.START_CLIENT_USER_SESSION, {
            error: stringifyLoggable(error),
          });
          return of(
            setAuthenticationStatusAction(
              AuthenticationResultStatusCode.FAILURE,
            ),
            startApplicationAccessAction.failure(),
          );
        }),
      );
    }),
  );
};
